import { exhaustiveCheck, isTruthy } from '@ardoq/common-helpers';
import {
  ColumnPinnedEvent,
  GridApi,
  IRowDragItem,
  RowClassParams,
  RowDragEndEvent,
  RowSelectionOptions,
  SelectionChangedEvent,
  SortChangedEvent,
  ValueSetterParams,
} from 'ag-grid-community';
import { RefObject } from 'react';
import {
  AnyRow,
  CellValueChangePayload,
  Column,
  GroupingColumn,
  MultiSelectableProps,
  ObjectColumn,
  PinnableProps,
  PinnedColumns,
  ReorderableProps,
  Row,
  SelectableProps,
  SortableProps,
} from './types';

export const AG_GRID_AUTO_COLUMN_ID = 'ag-Grid-AutoColumn';

export type ComparableValue = boolean | string | number | undefined;

export const areSetsEqual = <T>(a: Set<T>, b: Set<T>): boolean => {
  if (a.size !== b.size) return false;
  return a.intersection(b).size === a.size;
};

export const defaultComparator = (
  a: ComparableValue,
  b: ComparableValue
): number => {
  if (a === undefined) {
    return -1;
  } else if (b === undefined) {
    return 1;
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  const lowerA = String(a).toLowerCase();
  const lowerB = String(b).toLowerCase();

  // oxlint-disable-next-line ardoq-lint/no-restricted-properties
  return lowerA.localeCompare(lowerB);
};

// Tells AG Grid to leave the row order untouched. Used when the consumer has
// already sorted `rowData` according to the active sort descriptor.
export const noopComparator = (): number => 0;

// We use valueSetter instead of onValueChanged so AG Grid won't have internal state
// that is not in sync with the props.
// Called when ag-grid is done editing the cell, not on every change,
// this lets ag-grid handle focus as well as commit vs cancel edit.
export const getValueSetter =
  <RowData, RowId extends string = string>(
    onCellValueChange:
      | ((payload: CellValueChangePayload<RowData, RowId>) => void)
      | null
      | undefined
  ) =>
  (params: ValueSetterParams<Row<RowData, RowId>>): boolean => {
    if (onCellValueChange) {
      onCellValueChange({
        row: params.data,
        value: params.newValue,
      });
    }

    // Since the change will come back through the props, we tell AG Grid that
    // nothing has changed yet.
    return false;
  };

export const getRowDragText = <RowData>(
  params: IRowDragItem,
  api: GridApi | null,
  rowDragTextFn: (rows: RowData[]) => string
): string => {
  const rowNode = params.rowNode;
  if (!rowNode || !api) {
    return '';
  }

  const draggedRow = rowNode.data?.data;

  if (rowNode.group) {
    // We currently only support dragging single group rows
    return rowDragTextFn([draggedRow]);
  }

  if (rowNode.isSelected()) {
    return rowDragTextFn(api.getSelectedRows().map(row => row.data));
  }

  // Single row drag
  return rowDragTextFn([draggedRow]);
};

export const isGroupingColumn = <RowData, RowId extends string = string>(
  column: unknown
): column is GroupingColumn<RowData, RowId> => {
  return (
    typeof column === 'object' &&
    column !== null &&
    'expandedIds' in column &&
    'onRowExpansionToggle' in column
  );
};

export const isObjectColumn = <RowData, RowId extends string = string>(
  column: unknown
): column is ObjectColumn<RowData, keyof RowData, RowId> => {
  return (
    typeof column === 'object' &&
    column !== null &&
    'cellRenderer' in column &&
    !isGroupingColumn(column)
  );
};

export const getGroupingColumn = <RowData, RowId extends string = string>(
  columns: Column<RowData, RowId>[]
) =>
  columns.find(col => isGroupingColumn(col)) as
    | GroupingColumn<RowData, RowId>
    | undefined;

export const createGetRowClass =
  <RowData, RowId extends string = string>(
    highlightedRowIdsRef: RefObject<Set<string>>
  ) =>
  (row: RowClassParams<Row<RowData, RowId>>): string | string[] | undefined => {
    const classes: string[] = [];

    if (row?.data?.id && highlightedRowIdsRef.current.has(row.data.id)) {
      classes.push('ag-row-highlight');
    }

    if (row?.data?.status) {
      switch (row.data.status) {
        case 'error':
          classes.push('ag-row-error');
          break;
        case 'warning':
          classes.push('ag-row-warning');
          break;
        default:
          exhaustiveCheck(row.data.status);
      }
    }

    if (classes.length === 0) {
      return undefined;
    }

    return classes.length === 1 ? classes[0] : classes;
  };

const toSingleRowSelection = (): RowSelectionOptions => ({
  mode: 'singleRow',
  checkboxes: false,
  enableClickSelection: true,
});

const toMultiRowSelection = <RowId extends string = string, RowData = unknown>(
  selectable: MultiSelectableProps<RowId, RowData>
): RowSelectionOptions => ({
  mode: 'multiRow',
  groupSelects: selectable.groupSelects ?? 'self',
  ...(selectable.hideHeaderCheckbox && { headerCheckbox: false }),
  ...(selectable.enableRowClickSelection && {
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  }),
});

export const toRowSelectionOptions = <
  RowId extends string = string,
  RowData = unknown,
>(
  selectable: SelectableProps<RowId, RowData>
): RowSelectionOptions =>
  selectable.mode === 'single'
    ? toSingleRowSelection()
    : toMultiRowSelection(selectable);

export const getOnSelectionChanged =
  <RowData, RowId extends string = string>(
    selectableRef: RefObject<SelectableProps<RowId, RowData> | undefined>
  ) =>
  (event: SelectionChangedEvent<Row<RowData, RowId>>) => {
    const selectable = selectableRef.current;
    if (!selectable) {
      return;
    }

    // event.selectedRows also includes groups created by AG grid, which we currently don't support selection for.
    const selectedRows = event.api.getSelectedRows();

    // AG Grid types does not reflect that clicking a grouped row will return the row as undefined.
    const selectedIds = selectedRows
      .map(row => row?.id)
      .filter(isTruthy) as RowId[];

    if (selectable.mode === 'single') {
      selectable.onChange(selectedIds[0] ?? null);
    } else {
      selectable.onChange(selectedIds);
    }
  };

export const getOnSortChanged =
  <RowData, FullWidthRowData = never, RowId extends string = string>(
    sortableRef: RefObject<SortableProps | undefined>,
    groupingColumnId: string | null
  ) =>
  (event: SortChangedEvent<AnyRow<RowData, FullWidthRowData, RowId>>) => {
    const sortable = sortableRef.current;
    if (!sortable) {
      return;
    }

    const columns = event.columns;

    if (!columns) {
      sortable.onChange?.(null);
      return;
    }

    const column = columns.find(col => col.getSort() !== undefined);

    if (!column) {
      sortable.onChange?.(null);
      return;
    }

    const direction = column.getSort();

    if (!direction) {
      sortable.onChange?.(null);
      return;
    }

    const columnId = column.getId();
    sortable.onChange?.({
      column:
        columnId === AG_GRID_AUTO_COLUMN_ID && groupingColumnId !== null
          ? groupingColumnId
          : columnId,
      direction,
    });
  };

export const getOnColumnPinned =
  <RowData, RowId extends string = string>(
    pinnableRef: RefObject<PinnableProps<RowData> | undefined>
  ) =>
  (event: ColumnPinnedEvent<Row<RowData, RowId>>) => {
    const pinnable = pinnableRef.current;
    if (!pinnable) {
      return;
    }

    const pinnedColumns: PinnedColumns<RowData> = {};
    event.api.getAllDisplayedColumns().forEach(col => {
      const pinned = col.getPinned();
      if (pinned === 'left' || pinned === 'right') {
        pinnedColumns[col.getId() as keyof RowData] = pinned;
      }
    });
    pinnable.onChange?.(pinnedColumns);
  };

/**
 * AG Grid reports `overIndex` as -1 when the pointer is released past the last
 * row. Its managed row move still places the row last, so the last index is the
 * position the grid settled on.
 */
const getTargetRowIndex = (overIndex: number, rowCount: number): number => {
  if (overIndex < 0 || overIndex >= rowCount) {
    return rowCount - 1;
  }
  return overIndex;
};

export const getOnRowDragEnd =
  <RowData, RowId extends string = string>(
    reorderableRef: RefObject<ReorderableProps<RowData> | undefined>,
    rowsRef: RefObject<AnyRow<RowData, unknown, RowId>[]>
  ) =>
  (event: RowDragEndEvent<Row<RowData, RowId>>) => {
    const reorderable = reorderableRef.current;
    if (!reorderable) {
      return;
    }

    const rows = rowsRef.current;

    const sourceRowIndex = rows.findIndex(
      row => row.id === event.node.data?.id
    );
    if (sourceRowIndex === -1) {
      return;
    }

    const targetRowIndex = getTargetRowIndex(event.overIndex, rows.length);
    if (sourceRowIndex === targetRowIndex) {
      return;
    }

    reorderable.onReorder(sourceRowIndex, targetRowIndex);
  };

export const getReorderableProps = <RowData, RowId extends string = string>(
  reorderable: ReorderableProps<RowData> | undefined,
  reorderableRef: RefObject<ReorderableProps<RowData> | undefined>,
  rowsRef: RefObject<AnyRow<RowData, unknown, RowId>[]>
) => {
  const reorderableEnabled = reorderable !== undefined;
  const reorderableProps = reorderableEnabled
    ? {
        rowDragManaged: reorderableEnabled,
        onRowDragEnd: getOnRowDragEnd<RowData, RowId>(reorderableRef, rowsRef),
      }
    : undefined;

  return {
    reorderableEnabled,
    reorderableProps,
  };
};
